/**
 * Error Boundary Component
 * Catches JavaScript errors and displays a friendly error page
 * Also reports errors to Sentry for monitoring
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform, ActivityIndicator } from 'react-native';
import { useTranslation } from 'react-i18next';
import * as Updates from 'expo-updates';
import { createLogger } from '../utils/logger';
import { resetToInitialScreen } from '../navigation/AppNavigator';
import { useFontScale, useFontFamily } from '../hooks/useDisplaySettings';

const log = createLogger('ErrorBoundary');

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isRetrying: boolean;
}

/**
 * Props for the error fallback UI component.
 * Exported so Storybook can render it directly.
 */
export interface ErrorFallbackProps {
  error: Error | null;
  isRetrying: boolean;
  onRetry: () => void;
}

/**
 * Presentational error fallback page.
 * Uses plain RN StyleSheet with hardcoded colors — intentionally theme-independent
 * so it always renders reliably, even if the theme provider is broken.
 *
 * IMPORTANT: this renders OUTSIDE the app's providers (the ErrorBoundary sits
 * above them so it can catch their failures), so it must NOT depend on any
 * context that can throw. Font scale/family come from `useDisplaySettings`,
 * whose hooks return system defaults with no provider — unlike
 * `useDisplayTheme`/`useThemeColors`, which call `useColorMode()` and throw
 * "useColorMode must be used within a ColorModeProvider", crashing the
 * fallback itself (the secondary crash we used to see).
 */
export function ErrorFallback({ error, isRetrying, onRetry }: ErrorFallbackProps) {
  const { t } = useTranslation();
  const fontScale = useFontScale();
  const fontFamily = useFontFamily();
  const ffs = fontFamily ? { fontFamily } : undefined;
  const scaledIconSize = Math.round(80 * fontScale);

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <Text
          allowFontScaling={false}
          style={[
            styles.icon,
            {
              fontSize: 48 * fontScale,
              width: scaledIconSize,
              height: scaledIconSize,
              borderRadius: scaledIconSize / 2,
              lineHeight: scaledIconSize,
              ...ffs,
            },
          ]}
        >
          !
        </Text>
        <Text allowFontScaling={false} style={[styles.title, { fontSize: 24 * fontScale, ...ffs }]}>
          {t('errorBoundary.title')}
        </Text>
        <Text allowFontScaling={false} style={[styles.message, { fontSize: 16 * fontScale, lineHeight: 24 * fontScale, ...ffs }]}>
          {t('errorBoundary.message')}
        </Text>
        <Text allowFontScaling={false} style={[styles.suggestion, { fontSize: 14 * fontScale, ...ffs }]}>
          {t('errorBoundary.suggestion')}
        </Text>

        <TouchableOpacity
          style={[styles.button, isRetrying && styles.buttonDisabled]}
          onPress={onRetry}
          disabled={isRetrying}
        >
          {isRetrying ? (
            <ActivityIndicator color="#ffffff" size="small" />
          ) : (
            <Text allowFontScaling={false} style={[styles.buttonText, { fontSize: 16 * fontScale, ...ffs }]}>
              {t('errorBoundary.retryButton')}
            </Text>
          )}
        </TouchableOpacity>

        {__DEV__ && error && (
          <View style={styles.debugContainer}>
            <Text allowFontScaling={false} style={[styles.debugTitle, { fontSize: 12 * fontScale, ...ffs }]}>
              {t('errorBoundary.debugInfo')}
            </Text>
            <Text allowFontScaling={false} style={[styles.debugText, { fontSize: 12 * fontScale, ...ffs }]}>
              {error.message}
            </Text>
          </View>
        )}
      </View>
    </View>
  );
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, isRetrying: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error, isRetrying: false };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // log.error captures the exception to Sentry with the original Error object
    log.error('Caught an error', error, { componentStack: errorInfo.componentStack });
  }

  handleRetry = async (): Promise<void> => {
    this.setState({ isRetrying: true });

    if (Platform.OS === 'web') {
      // On web, reload the page to get latest version
      window.location.reload();
      return;
    }

    // On native, check for OTA updates before re-rendering
    try {
      const update = await Updates.checkForUpdateAsync();
      if (update.isAvailable) {
        // Download and apply the update
        await Updates.fetchUpdateAsync();
        // Restart the app with the new code
        await Updates.reloadAsync();
      } else {
        // No update available, clear error and navigate to initial screen
        this.setState({ hasError: false, error: null, isRetrying: false }, () => {
          resetToInitialScreen();
        });
      }
    } catch (e) {
      // expo-updates doesn't work in dev mode, clear error and navigate to initial screen
      log.debug('Updates check failed (expected in dev)', { error: String(e) });
      this.setState({ hasError: false, error: null, isRetrying: false }, () => {
        resetToInitialScreen();
      });
    }
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error}
          isRetrying={this.state.isRetrying}
          onRetry={this.handleRetry}
        />
      );
    }

    return this.props.children;
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  content: {
    maxWidth: 400,
    alignItems: 'center',
  },
  icon: {
    fontSize: 48,
    fontWeight: 'bold',
    color: '#dc3545',
    backgroundColor: '#f8d7da',
    width: 80,
    height: 80,
    borderRadius: 40,
    textAlign: 'center',
    lineHeight: 80,
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: '600',
    color: '#212529',
    marginBottom: 16,
    textAlign: 'center',
  },
  message: {
    fontSize: 16,
    color: '#6c757d',
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 8,
  },
  suggestion: {
    fontSize: 14,
    color: '#6c757d',
    textAlign: 'center',
    marginBottom: 32,
  },
  button: {
    backgroundColor: '#0d6efd',
    paddingVertical: 14,
    paddingHorizontal: 32,
    borderRadius: 8,
    alignItems: 'center',
  },
  buttonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  debugContainer: {
    marginTop: 32,
    padding: 16,
    backgroundColor: '#fff3cd',
    borderRadius: 8,
    width: '100%',
  },
  debugTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#856404',
    marginBottom: 8,
  },
  debugText: {
    fontSize: 12,
    color: '#856404',
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
  },
});
